<template>
  <div
    :class="classes(n(), [padded, n('--padded')])"
    role="region"
    aria-label="表单预览"
  >
    <!-- 头部区域（条件渲染） -->
    <div
      v-if="showHeader"
      :class="n('header')"
      role="group"
      aria-label="主要信息"
    >
      <span :class="n('header-key')">{{ headerKey }}</span>
      <div :class="n('header-value')">
        <slot name="headerValue" />
      </div>
    </div>

    <!-- 中间内容区域（必选） -->
    <div
      :class="n('content')"
      role="list"
      aria-label="详细信息列表"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        :class="getItemClasses(index)"
        role="listitem"
        :aria-label="`${item.key}: ${item.value}`"
      >
        <span :class="n('item-key')">{{ item.key }}</span>
        <div :class="getValueClasses(item.valueType, item.displayType)">
          <slot
            name="item-value"
            :item="item"
            :index="index"
          >
            {{ item.value }}
          </slot>
        </div>
      </div>
    </div>

    <!-- 底部按钮区域（条件渲染） -->
    <div
      v-if="showFooter"
      :class="n('footer')"
      role="group"
      aria-label="操作按钮"
    >
      <div
        v-for="(button, index) in buttons"
        :key="index"
        :class="getButtonClasses(button.type)"
        type="button"
        @click="handleButtonClick(index, button)"
        @keydown.enter="handleButtonClick(index, button)"
        @keydown.space.prevent="handleButtonClick(index, button)"
      >
        {{ button.text }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import type { ButtonConfig } from './form-preview';

const { name, n, classes } = createNamespace('form-preview');

export default defineComponent({
  name,
  props,
  emits,
  setup(props, { emit }) {
    // 计算属性：判断是否显示头部区域
    const showHeader = computed(() => {
      return !!props.headerKey;
    });

    // 计算属性：判断是否显示底部区域
    const showFooter = computed(() => {
      return props.buttons && props.buttons.length > 0;
    });

    // 获取 item 的样式类
    const getItemClasses = (index: number) => {
      const total = props.items?.length || 0;
      const itemClasses = [n('item'), n(`item--align-${props.align}`)];

      // 第一个 item 或只有一个 item 时，顶部有分割线
      if (index === 0) {
        itemClasses.push(n('item--with-top-divider'));
      }

      // 最后一个 item 或只有一个 item 时，底部有分割线
      if (index === total - 1) {
        itemClasses.push(n('item--with-bottom-divider'));
      }

      return itemClasses;
    };

    // 获取 value 的样式类
    const getValueClasses = (valueType?: string, displayType?: string) => {
      const valueClasses = [n('item-value'), n(`item-value--align-${props.align}`)];
      // 显示类型：amount 使用金额字体
      if (displayType === 'amount') {
        valueClasses.push(n('item-value--amount'));
      }
      // 颜色类型
      if (valueType && valueType !== 'default') {
        valueClasses.push(n(`item-value--${valueType}`));
      }
      return valueClasses;
    };

    // 获取按钮的样式类
    const getButtonClasses = (type?: string) => {
      const buttonClasses = [n('footer-button')];
      buttonClasses.push(n(`footer-button--${type || 'primary'}`));
      return buttonClasses;
    };

    // 按钮点击处理
    const handleButtonClick = (index: number, button: ButtonConfig) => {
      // 触发事件
      emit('button-click', index, button);
      // 执行按钮的 onClick 回调
      button.onClick?.();
    };

    return {
      n,
      classes,
      showHeader,
      showFooter,
      getItemClasses,
      getValueClasses,
      getButtonClasses,
      handleButtonClick,
    };
  },
});
</script>

